// ============================================================
// MOONSHINE — Hero.js        LYRAEI & LAOSTRE, in the woods
//
// Borrowed from Hooked's HeroAngler.js (borrow-first, 2026-10-23).
//
// THE LAW OF ONE BODY. lyraeiModel3D.jsx / laostreModel3D.jsx /
// sculptGate.jsx and both glbs are BYTE-IDENTICAL copies of
// Terror's (md5-checked at scaffold). NEVER edit them here.
// They own the body, we own the pose: this file only picks the
// body, sizes it, and hands it the same ctl = { facing, speed }.
//
// THE FAIL-SAFE LAW. While a glb loads, if it fails, or if
// <FrameWatch/> sees the screen can't keep up, the painted stand-in
// below stands in. The woods never show nobody.
// ============================================================
import { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import { LyraeiSculpted } from "./lyraeiModel3D";
import { LaostreSculpted } from "./laostreModel3D";
import { failSafe } from "./sculptGate";

// Same proportions the Pier uses (a person is ~1.5 m): Lyraei 1.3, Laostre 1.6, x 1.5/1.3.
const PIER_PERSON = 1.5 / 1.3;
export const LYRAEI_H = 1.3 * PIER_PERSON;
export const LAOSTRE_H = 1.6 * PIER_PERSON;

// the stand-in: a plain painted figure (placeholder; the sculpts replace it the moment they load)
function Painted({ ctl, h = 1.5 }) {
  const g = useRef();
  useFrame(({ clock }) => {
    const o = g.current; if (!o) return;
    const f = ctl.facing || { x: 0, y: 1 };
    o.rotation.y = Math.atan2(f.x, f.y);
    o.position.y = ctl.speed ? Math.abs(Math.sin(clock.elapsedTime * 9)) * 0.05 : 0;
  });
  const k = h / 1.5;
  return (
    <group ref={g} scale={k}>
      <mesh position={[0, 0.62, 0]}><capsuleGeometry args={[0.24, 0.62, 4, 10]} /><meshStandardMaterial color="#6a4f8f" roughness={0.8} /></mesh>
      <mesh position={[0, 1.28, 0]}><sphereGeometry args={[0.2, 14, 12]} /><meshStandardMaterial color="#e8c9a8" roughness={0.7} /></mesh>
      <mesh position={[0, 1.4, -0.02]}><sphereGeometry args={[0.215, 14, 12, 0, Math.PI * 2, 0, Math.PI * 0.55]} /><meshStandardMaterial color="#2b1d3a" roughness={0.9} /></mesh>
    </group>
  );
}
const LyraeiHere = failSafe(LyraeiSculpted, Painted, LYRAEI_H);
const LaostreHere = failSafe(LaostreSculpted, Painted, LAOSTRE_H);

// `posRef` places it, `yawRef` is atan2(dx, dz) (the Pier / Hooked convention).
export function Hero({ gender = "f", posRef, yawRef, speedRef, lantern = true }) {
  const g = useRef();
  const ctl = useMemo(() => ({ facing: { x: 0, y: 1 }, speed: 0 }), []);
  const shadowed = useRef(0);
  useFrame(() => {
    const o = g.current; if (!o) return;
    if (posRef.current) o.position.copy(posRef.current);
    const y = yawRef.current || 0;
    ctl.speed = speedRef.current > 0.05 ? 1 : 0;
    ctl.facing.x = Math.sin(y); ctl.facing.y = Math.cos(y);
    // the sculpted bodies arrive async and can't cast shadows themselves (files never edited)
    if (shadowed.current < 240 && (++shadowed.current % 30 === 0)) {
      o.traverse((m) => { if (m.isMesh && !/_ink$/.test(m.name)) m.castShadow = true; });
    }
  });
  return (
    <group ref={g}>
      {gender === "m" ? <LaostreHere ctl={ctl} /> : <LyraeiHere ctl={ctl} />}
      {/* a lantern at the hip: the woods are dark and she is the one holding the light */}
      {lantern && <pointLight position={[0.34, 0.95, 0.12]} intensity={5} distance={11} decay={1.4} color="#ffc978" castShadow={false} />}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.012, 0]} raycast={() => null}>
        <circleGeometry args={[0.4, 20]} />
        <meshBasicMaterial color="#000000" transparent opacity={0.4} depthWrite={false} />
      </mesh>
    </group>
  );
}

const BODY_KEY = "moonshine_body_v1";
export const loadBody = () => { try { return localStorage.getItem(BODY_KEY) === "m" ? "m" : "f"; } catch (e) { return "f"; } };
export const saveBody = (g) => { try { localStorage.setItem(BODY_KEY, g); } catch (e) { /* private mode */ } };
